import {
  AchievementClaim, AchievementDef, AchievementFacts, AchievementProgress, AchievementsJson, BattleFacts, GearItem, LifetimeStats, StatsJson,
  SimConstants, achievementSeed, masteryTier, progressOf, starCount, STARS_PER_LEVEL,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import { GameEventHub, gameEvents } from "./gameEvents";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Achievements, lifetime stats and tower mastery (FINISH_PLAN WP7, M19/M20; DESIGN §9.7) as use cases. The state lives
 * in two save sections — `achievements` (what is done, claimed, announced; the worn title) and `stats` (kills, wins,
 * damage) — beside the catalog, which saves them with the rest of the progress (LevelCatalog.applyTo).
 */

/** A section's raw JSON when it is an object; the domain `fromJson` validates the fields. */
function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const achievementsSection = registerSaveSection<AchievementsJson>("achievements", asObject, (v) => v);
export const statsSection = registerSaveSection<StatsJson>("stats", asObject, (v) => v);

export class AchievementsState {
  constructor(readonly progress: AchievementProgress, readonly stats: LifetimeStats) {}
  static fresh(): AchievementsState { return new AchievementsState(new AchievementProgress(), new LifetimeStats()); }
  static fromSave(save: HasSections): AchievementsState {
    return new AchievementsState(AchievementProgress.fromJson(achievementsSection.read(save)), LifetimeStats.fromJson(statsSection.read(save)));
  }
  applyTo(save: HasSections): void {
    achievementsSection.write(save, this.progress.toJson());
    statsSection.write(save, this.stats.toJson());
  }
}

/** One row of the Achievements sheet. */
export interface AchievementRow {
  readonly def: AchievementDef;
  readonly have: number;
  readonly target: number;
  readonly done: boolean;
  readonly claimed: boolean;
}

/** A claim as the sheet shows it: what it paid and the gear its chest held (already banked). */
export interface AchievementPayout extends AchievementClaim { readonly gear: readonly GearItem[] }

/**
 * How a battle ran, for the `battleWon` announcement (MP-27): the run's mode id (app records.ts battleMode), its
 * length in sim ticks, the stars it met and when it ended.
 */
export interface BattleRun { readonly mode: string; readonly ticks: number; readonly stars: number; readonly now: number }

/** Sim ticks as milliseconds (the clear time a record keeps): deterministic, from the tick count. */
export function ticksToMs(ticks: number): number { return Math.round((ticks * 1000) / SimConstants.TicksPerSecond); }

export class Achievements {
  constructor(private readonly catalog: LevelCatalog, private readonly events: GameEventHub = gameEvents) {}

  private get state(): AchievementsState { return this.catalog.achievements; }
  get progress(): AchievementProgress { return this.state.progress; }
  get stats(): LifetimeStats { return this.state.stats; }
  get defs(): readonly AchievementDef[] { return this.catalog.balance.achievements.list; }

  /** Every metric an achievement can watch, read from the save's progress, profile, stats and endgame. */
  facts(): AchievementFacts {
    const c = this.catalog, B = c.balance, p = c.profile, s = this.stats;
    let stars = 0, three = 0, perfect = 0;
    for (let l = 1; l <= c.levelCount; l++) { const n = starCount(c.starsFor(l)); stars += n; if (n === STARS_PER_LEVEL) three++; }
    for (let ch = 0; ch < c.chapterCount; ch++) { const t = c.totalStars(ch); if (t.have === t.max) perfect++; }
    const towers = B.towers.filter((t) => !t.isBase);
    const owned = c.ownedTowers();
    const top = B.achievements.mastery.length - 1;
    return {
      levelsCleared: c.highestLevelCleared, stars, threeStarLevels: three, perfectChapters: perfect, bossesDefeated: s.bossesDefeated(B),
      kills: s.totalKills, airKills: s.airKills, eliteKills: s.eliteKills, wins: s.wins, noLeakWins: s.noLeakWins,
      towerLevel: Math.max(0, ...towers.map((t) => p.towerLevels[t.index])), baseLevel: p.baseLevel,
      towersOwned: towers.filter((t) => owned[t.index]).length, builders: p.buildersOwned,
      towersMastered: top < 0 ? 0 : towers.filter((t) => this.mastery(t.id) === top).length,
      bestRarity: Math.max(0, ...p.gear.map((g) => g.rarity)), itemLevel: Math.max(0, ...p.gear.map((g) => g.level)),
      fusions: p.forge.fused.reduce((a, n) => a + n, 0), fusedRarity: Math.max(0, ...p.forge.fused.map((n, r) => (n > 0 ? r : 0))), raisedLevels: p.forge.raisedLevels,
      mythics: new Set(p.gear.filter((g) => g.mythicEffect >= 0).map((g) => g.mythicEffect)).size,
      heroicStars: c.endgame.trials.heroicCount, trialWins: s.trialWins,
      ironStars: c.modes.ironStarCount, endlessBest: c.modes.bestWaves,
      raresSeen: s.raresSeenCount(B), raresKilled: s.raresKilled(B),
      wyrmsSeen: s.wyrmsSeen, wyrmsKilled: s.wyrmsKilled,
    };
  }

  /** Marks what the save now meets as done; returns the newly done (the caller saves and announces them). */
  sync(now: number): AchievementDef[] { return this.progress.sync(this.catalog.balance.achievements, this.facts(), now); }

  /** The sheet's rows, in data order. */
  rows(): AchievementRow[] {
    const f = this.facts();
    return this.defs.map((def) => {
      const done = this.progress.isDone(def.id);
      const p = progressOf(def, f);
      return { def, have: done ? p.target : p.have, target: p.target, done, claimed: this.progress.isClaimed(def.id) };
    });
  }

  /** Done and unclaimed: the More row's red count. */
  claimable(): number { return this.progress.claimable(this.catalog.balance.achievements).length; }

  /** Claims one: pays it once (the chest banked into the profile) or null when it is not claimable. */
  claim(id: string, now: number): AchievementPayout | null {
    const def = this.defs.find((d) => d.id === id);
    if (!def) return null;
    const c = this.catalog;
    const claim = this.progress.claim(c.balance, def, {
      profile: c.profile, wallet: c.economy.wallet, level: Math.max(1, c.highestLevelCleared), seed: achievementSeed(c.playerId, def.id), at: now, ref: `achievement.${def.id}`,
    });
    if (!claim) return null;
    return { ...claim, gear: claim.paid.chest ? c.bankChest(claim.paid.chest) : [] };
  }

  /**
   * The end-of-battle use case: counts a finished battle once per `key` (false when it was already counted) and, for a
   * win, announces `battleWon` on the game events with `run`'s mode and clear time — the one place it is emitted.
   */
  recordBattle(facts: BattleFacts, key: string, run?: BattleRun): boolean {
    if (!this.stats.record(facts, key)) return false;
    if (facts.won && run) this.events.emit("battleWon", { mode: run.mode, level: facts.level, ms: ticksToMs(run.ticks), stars: run.stars, key, at: run.now });
    return true;
  }

  /** A tower type's mastery tier (index into the data's tiers; −1 before the first). */
  mastery(towerId: string): number { return masteryTier(this.catalog.balance.achievements.mastery, this.stats.killsBy(towerId)); }

  /** Titles unlocked, and the one worn. */
  titles(): string[] { return this.progress.titles(this.catalog.balance.achievements); }
  get title(): string { return this.progress.title; }
  wearTitle(title: string): boolean { return this.progress.wearTitle(this.catalog.balance.achievements, title); }
}
